
import React, { useState, useEffect } from 'react';
import {
  DashboardOutlined,
  BankOutlined,
  UserOutlined,
  SettingOutlined,
  CalendarOutlined,
  TrophyOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  LockOutlined,
  PictureOutlined,
  CrownOutlined,
  ArrowLeftOutlined,
  FormOutlined,
  UsergroupAddOutlined,
  DownOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useNavigate } from '../hooks/useNavigate';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, Modal } from 'antd';
import { RootState } from '../redux/store';
import { usePermissions } from '../hooks/usePermissions';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { BrandLogo } from '../components/ui';
import { GET } from '../utils/apiRequest';
import API from '../config/apis';
import { setAssignments, setSelectedAssignment, clearSelectedAssignment, clearTeamLeadState, TeamLeadAssignment } from '../redux/slice/teamLeadSlice';
import { setJudgeFests, setSelectedJudgeFest, clearSelectedJudgeFest, JudgeFest } from '../redux/slice/judgeSlice';
import { setSelectedCoordinatorFest, clearSelectedCoordinatorFest, CoordinatorFest } from '../redux/slice/coordinatorSlice';
import { setSelectedVolunteerFest, clearSelectedVolunteerFest, VolunteerFest } from '../redux/slice/volunteerSlice';
import { clearCustomRoleFest } from '../redux/slice/customRoleSlice';
import ProfileDrawer from '../components/profile/ProfileDrawer';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const toAbsUrl = (path: string | null | undefined) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_BASE}${path}`;
};

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  module?: string;
  action?: 'view' | 'create' | 'edit' | 'delete' | 'publish';
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

interface SideBarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  user: any;
  onLogout: () => void;
  /** Mobile: sidebar shown as overlay */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const getNavGroups = (role: string): NavGroup[] => {
  const dashboard: NavItem = {
    key: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardOutlined />,
  };

  switch (role) {
    case 'SUPER_ADMIN':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Management',
          items: [
            { key: 'institutions', label: 'Institutions', icon: <BankOutlined /> },
            { key: 'subscriptions', label: 'Subscriptions', icon: <CrownOutlined /> },
            { key: 'users', label: 'Users', icon: <UserOutlined /> },
          ],
        },
        {
          title: 'Templates',
          items: [
            { key: 'templates', label: 'Templates', icon: <FileTextOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'INSTITUTION_ADMIN':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Management',
          items: [
            { key: 'users', label: 'Users', icon: <UserOutlined />, module: 'users', action: 'view' },
            { key: 'roles', label: 'Roles & Permissions', icon: <SafetyCertificateOutlined />, module: 'users', action: 'view' },
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined />, module: 'fests', action: 'view' },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'subscription', label: 'Subscription', icon: <CrownOutlined /> },
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'FEST_ADMIN':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Management',
          items: [
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined />, module: 'fests', action: 'view' },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'ITEM_COORDINATOR_NO_FEST':
      return [
        {
          title: 'Overview',
          items: [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
          ],
        },
        {
          title: 'Management',
          items: [
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'ITEM_COORDINATOR':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Event Management',
          items: [
            { key: 'events', label: 'My Events', icon: <TrophyOutlined />, module: 'events', action: 'view' },
            { key: 'participants/event-registration', label: 'Event Assignment', icon: <UsergroupAddOutlined />, module: 'participants', action: 'edit' },
            { key: 'schedule', label: 'Schedule', icon: <CalendarOutlined />, module: 'schedules', action: 'view' },
            { key: 'results', label: 'Results', icon: <BarChartOutlined />, module: 'results', action: 'view' },
            { key: 'championship', label: 'Championship', icon: <CrownOutlined />, module: 'championship', action: 'view' },
            { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
          ],
        },
        {
          title: 'Operations',
          items: [
            { key: 'volunteers/assignments', label: 'Volunteers', icon: <TeamOutlined />, module: 'volunteers', action: 'view' },
            { key: 'judges/assignments', label: 'Judges', icon: <UserOutlined />, module: 'judges', action: 'view' },
            { key: 'announcements', label: 'Announcements', icon: <BellOutlined />, module: 'announcements', action: 'view' },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'JUDGE_NO_FEST':
      return [
        {
          title: 'Overview',
          items: [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
          ],
        },
        {
          title: 'Management',
          items: [
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'JUDGE':
      return [
        {
          title: 'Judging',
          items: [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
            { key: 'events', label: 'My Events', icon: <CalendarOutlined /> },
            { key: 'mark-entry', label: 'Mark Entry', icon: <FormOutlined /> },
            { key: 'schedule', label: 'Schedule', icon: <CalendarOutlined />, module: 'schedules', action: 'view' },
            { key: 'results', label: 'Results', icon: <BarChartOutlined /> },
            { key: 'championship', label: 'Championship', icon: <CrownOutlined /> },
            { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
          ],
        },
        {
          title: 'Account',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'PARTICIPANT':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Personal',
          items: [
            { key: 'dashboard', label: 'My Dashboard', icon: <DashboardOutlined /> },
            { key: 'events', label: 'My Events', icon: <CalendarOutlined /> },
            { key: 'results', label: 'My Results', icon: <TrophyOutlined /> },
            { key: 'certificates', label: 'Certificates', icon: <SafetyCertificateOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [{ key: 'settings', label: 'Settings', icon: <SettingOutlined /> }],
        },
      ];
    case 'VOLUNTEER_NO_FEST':
      return [
        {
          title: 'Overview',
          items: [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
          ],
        },
        {
          title: 'Management',
          items: [
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'VOLUNTEER':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'My Tasks',
          items: [
            { key: 'assignments', label: 'My Assignments', icon: <CalendarOutlined /> },
            { key: 'schedule', label: 'Schedule', icon: <CalendarOutlined />, module: 'schedules', action: 'view' },
            { key: 'announcements', label: 'Announcements', icon: <BellOutlined /> },
            { key: 'championship', label: 'Championship', icon: <CrownOutlined />, module: 'championship', action: 'view' },
            { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'TEAM_LEAD_NO_FEST':
      return [
        {
          title: 'Overview',
          items: [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
          ],
        },
        {
          title: 'Management',
          items: [
            { key: 'my-fests', label: 'My Fests', icon: <CalendarOutlined /> },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'TEAM_LEAD':
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'My Team',
          items: [
            { key: 'participants', label: 'Participants', icon: <TeamOutlined />, module: 'participants', action: 'view' },
            { key: 'event-registration', label: 'Event Assignment', icon: <UsergroupAddOutlined />, module: 'participants', action: 'edit' },
            { key: 'schedule', label: 'Schedule', icon: <CalendarOutlined />, module: 'schedules', action: 'view' },
            { key: 'results', label: 'Results', icon: <TrophyOutlined />, module: 'results', action: 'view' },
            { key: 'championship', label: 'Championship', icon: <CrownOutlined />, module: 'championship', action: 'view' },
            { key: 'reports', label: 'Reports', icon: <FileTextOutlined /> },
          ],
        },
        {
          title: 'General',
          items: [
            { key: 'announcements', label: 'Announcements', icon: <BellOutlined />, module: 'announcements', action: 'view' },
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'CUSTOM':
      // Fest-scoped items (visible after selecting a fest)
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Fest Management',
          items: [
            { key: 'events', label: 'Events', icon: <TrophyOutlined />, module: 'events', action: 'view' },
            { key: 'categories', label: 'Categories', icon: <TagsOutlined />, module: 'categories', action: 'view' },
            { key: 'participants', label: 'Participants', icon: <UserOutlined />, module: 'participants', action: 'view' },
            { key: 'teams', label: 'Teams', icon: <TeamOutlined />, module: 'teams', action: 'view' },
            { key: 'venues', label: 'Venues', icon: <EnvironmentOutlined />, module: 'venues', action: 'view' },
            { key: 'judges/assignments', label: 'Judges', icon: <UserOutlined />, module: 'judges', action: 'view' },
            { key: 'volunteers/assignments', label: 'Volunteers', icon: <TeamOutlined />, module: 'volunteers', action: 'view' },
            { key: 'announcements', label: 'Announcements', icon: <BellOutlined />, module: 'announcements', action: 'view' },
            { key: 'results', label: 'Results', icon: <BarChartOutlined />, module: 'results', action: 'view' },
            { key: 'templates', label: 'Templates', icon: <FileTextOutlined />, module: 'poster_templates', action: 'view' },
          ],
        },
        {
          title: 'Content',
          items: [
            { key: 'users', label: 'Users', icon: <UserOutlined />, module: 'users', action: 'view' },
            { key: 'reports', label: 'Reports', icon: <BarChartOutlined />, module: 'reports', action: 'view' },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    case 'CUSTOM_NO_FEST':
      // No fest selected yet — show only non-fest pages + fest selector
      return [
        { title: 'Overview', items: [dashboard] },
        {
          title: 'Navigation',
          items: [
            { key: 'fests', label: 'Fests', icon: <CalendarOutlined /> },
          ],
        },
        {
          title: 'Content',
          items: [
            { key: 'users', label: 'Users', icon: <UserOutlined />, module: 'users', action: 'view' },
            { key: 'reports', label: 'Reports', icon: <BarChartOutlined />, module: 'reports', action: 'view' },
          ],
        },
        {
          title: 'System',
          items: [
            { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
          ],
        },
      ];
    default:
      return [{ title: 'Overview', items: [dashboard] }];
  }
};

const getFestManageNavGroups = (): NavGroup[] => [
  {
    title: 'Fest Management',
    items: [
      { key: 'overview', label: 'Overview', icon: <DashboardOutlined /> },
      { key: 'categories', label: 'Categories', icon: <TagsOutlined />, module: 'categories', action: 'view' },
      { key: 'groups', label: 'Houses / Teams', icon: <TeamOutlined />, module: 'teams', action: 'view' },
      { key: 'events', label: 'Events', icon: <TrophyOutlined />, module: 'events', action: 'view' },
      { key: 'participants', label: 'Participants', icon: <UserOutlined />, module: 'participants', action: 'view' },
      { key: 'participants/event-assignment', label: 'Event Assignment', icon: <UsergroupAddOutlined />, module: 'participants', action: 'edit' },
      { key: 'schedule', label: 'Schedule', icon: <CalendarOutlined />, module: 'schedules', action: 'view' },
      { key: 'participants/code-letters', label: 'Code Letters', icon: <TagsOutlined />, module: 'participants', action: 'edit' },
      { key: 'tabulation', label: 'Mark Entry', icon: <FormOutlined />, module: 'results', action: 'edit' },
      { key: 'results', label: 'Results', icon: <BarChartOutlined />, module: 'results', action: 'view' },
    ],
  },
  {
    title: 'Operations & Results',
    items: [
      { key: 'venues', label: 'Venues', icon: <EnvironmentOutlined />, module: 'venues', action: 'view' },
      { key: 'volunteers', label: 'Volunteers', icon: <TeamOutlined />, module: 'volunteers', action: 'view' },
      { key: 'judges', label: 'Judges', icon: <UserOutlined />, module: 'judges', action: 'view' },
      { key: 'announcements', label: 'Announcements', icon: <BellOutlined />, module: 'announcements', action: 'view' },
      { key: 'championship', label: 'Championship', icon: <CrownOutlined />, module: 'results', action: 'view' },
      { key: 'templates', label: 'Templates', icon: <FileTextOutlined />, module: 'poster_templates', action: 'view' },
      { key: 'certificates', label: 'Certificates', icon: <SafetyCertificateOutlined />, module: 'certificates', action: 'view' },
      { key: 'posters', label: 'Poster Gallery', icon: <PictureOutlined />, module: 'certificates', action: 'view' },
      { key: 'reports', label: 'Reports', icon: <FileTextOutlined />, module: 'reports', action: 'view' },
    ],
  },
];

const deriveFestStatus = (fest: { status: string; startDate: string; endDate: string }) => {
  const now = new Date();
  const start = new Date(fest.startDate); start.setHours(0, 0, 0, 0);
  const end = new Date(fest.endDate); end.setHours(23, 59, 59, 999);

  if (fest.status === 'CANCELLED') return { label: 'CANCELLED', color: 'red' as const };
  if (now > end) return { label: 'COMPLETED', color: 'purple' as const };
  if (now < start) return { label: 'UPCOMING', color: 'blue' as const };
  if (fest.status === 'ACTIVE') return { label: 'ACTIVE', color: 'green' as const };
  return { label: fest.status, color: 'orange' as const };
};

const getBasePath = (role: string): string => {
  switch (role) {
    case 'SUPER_ADMIN':
      return '/superadmin';
    case 'INSTITUTION_ADMIN':
      return '/admin';
    case 'ITEM_COORDINATOR':
      return '/coordinator';
    case 'FEST_ADMIN':
      return '/admin';
    case 'JUDGE':
      return '/judge';
    case 'PARTICIPANT':
      return '/participant';
    case 'VOLUNTEER':
    case 'VOLUNTEER_NO_FEST':
      return '/volunteer';
    case 'TEAM_LEAD':
      return '/team-lead';
    default:
      return '/admin';
  }
};

const getRoleLabel = (role: string): string => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'INSTITUTION_ADMIN':
      return 'Institution Admin';
    case 'FEST_ADMIN':
      return 'Fest Admin';
    case 'ITEM_COORDINATOR':
      return 'Item Coordinator';
    case 'JUDGE':
      return 'Judge';
    case 'PARTICIPANT':
      return 'Participant';
    case 'VOLUNTEER':
      return 'Volunteer';
    case 'TEAM_LEAD':
      return 'Team Lead';
    default:
      return role;
  }
};

const SideBar: React.FC<SideBarProps> = ({ collapsed, onCollapse, user, onLogout, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [changeFestPending, setChangeFestPending] = useState<{ action: () => void; festName: string } | null>(null);
  const confirmChangeFest = (festName: string, action: () => void) => setChangeFestPending({ action, festName });
  const [institutionLogo, setInstitutionLogo] = useState<string | null>(null);
  const role = user?.role || '';

  const teamLeadAssignments = useSelector((state: RootState) => (state as any).TeamLead?.assignments as TeamLeadAssignment[] ?? []);
  const selectedAssignment = useSelector((state: RootState) => (state as any).TeamLead?.selectedAssignment as TeamLeadAssignment | null ?? null);

  const selectedJudgeFest = useSelector((state: RootState) => (state as any).Judge?.selectedFest as JudgeFest | null ?? null);
  const selectedCoordinatorFest = useSelector((state: RootState) => (state as any).Coordinator?.selectedFest as CoordinatorFest | null ?? null);
  const selectedVolunteerFest = useSelector((state: RootState) => (state as any).Volunteer?.selectedFest as VolunteerFest | null ?? null);

  useEffect(() => {
    if (role === 'INSTITUTION_ADMIN') {
      const instId = (user as any)?.institution_id;
      if (instId) {
        GET(`${API.INSTITUTIONS}/${instId}`).then(res => {
          if (res.status && res.data) {
            const inst = (res.data as any).data || res.data;
            if (inst.logo) setInstitutionLogo(inst.logo);
          }
        });
      }
    }
    if (role === 'TEAM_LEAD') {
      Promise.all([GET(API.FESTS), GET(API.TEAM_MY_ASSIGNMENTS)]).then(([festsRes, assignRes]) => {
        const assignMap = new Map<string, { id: string; teamId: string; teamName: string; teamCode?: string; teamColor?: string }>();
        if (assignRes.status && assignRes.data) {
          const raw: any[] = (assignRes.data as any).data || assignRes.data || [];
          raw.forEach((a: any) => {
            if (!assignMap.has(a.festId)) {
              assignMap.set(a.festId, {
                id: a.id ?? '',
                teamId: a.teamId,
                teamName: a.team?.name || '',
                teamCode: a.team?.code,
                teamColor: a.team?.color,
              });
            }
          });
        }
        if (festsRes.status && festsRes.data) {
          const raw = festsRes.data?.data || festsRes.data;
          const festsList: any[] = raw?.rows || (Array.isArray(raw) ? raw : []);
          const mapped: TeamLeadAssignment[] = festsList
            .filter((f: any) => assignMap.has(f.id))
            .map((f: any) => {
              const asgn = assignMap.get(f.id)!;
              return {
                id: asgn.id,
                teamId: asgn.teamId,
                festId: f.id,
                teamName: asgn.teamName,
                teamCode: asgn.teamCode,
                teamColor: asgn.teamColor,
                festName: f.name,
                festLogo: f.logo,
                festStatus: f.status,
                festStartDate: f.startDate,
                festEndDate: f.endDate,
              };
            });
          dispatch(setAssignments(mapped));
        }
      });
    }
    if (role === 'JUDGE') {
      GET(API.JUDGE_FESTS).then(res => {
        if (res.status) {
          const data: JudgeFest[] = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          dispatch(setJudgeFests(data));
        }
      });
    }
  }, [role, (user as any)?.institution_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const basePath = role === 'CUSTOM' && (user as any)?.roleName
    ? `/${(user as any).roleName.toLowerCase().replace(/\s+/g, '-')}`
    : getBasePath(role);

  // Fest manage mode detection
  const festManageMatch = location.pathname.match(/\/fests\/([^/]+)\/manage/);
  const isFestManage = !!festManageMatch;
  const managedFestId = festManageMatch?.[1];
  const currentFest = useSelector((state: RootState) => state.Fest.currentFest);

  // Coordinator/Volunteer/CustomRole with a selected fest get a fest-scoped base path
  const selectedCustomRoleFest = useSelector((state: RootState) => (state as any).CustomRole?.selectedFest ?? null);

  const coordinatorFestBasePath = (role === 'ITEM_COORDINATOR' && selectedCoordinatorFest)
    ? `/coordinator/fests/${selectedCoordinatorFest.id}`
    : null;
  const volunteerFestBasePath = (role === 'VOLUNTEER' && selectedVolunteerFest)
    ? `/volunteer/fests/${selectedVolunteerFest.id}`
    : null;
  const customRoleFestBasePath = (role === 'CUSTOM' && selectedCustomRoleFest)
    ? `${basePath}/fests/${selectedCustomRoleFest.id}`
    : null;

  const effectiveBasePath = isFestManage
    ? `${basePath}/fests/${managedFestId}/manage`
    : coordinatorFestBasePath ?? volunteerFestBasePath ?? customRoleFestBasePath ?? basePath;

  const festManageSuffix = isFestManage ? (location.pathname.split('/manage/')[1] || 'overview') : '';

  // For non-fest-manage: get the path suffix relative to effectiveBasePath (supports multi-segment keys like volunteers/assignments)
  const nonManagePathSuffix = !isFestManage && location.pathname.startsWith(effectiveBasePath + '/')
    ? location.pathname.slice(effectiveBasePath.length + 1).split('?')[0]
    : location.pathname.split('/').pop() || 'dashboard';

  const effectiveRole = role === 'TEAM_LEAD' && !selectedAssignment
    ? 'TEAM_LEAD_NO_FEST'
    : role === 'JUDGE' && !selectedJudgeFest
      ? 'JUDGE_NO_FEST'
      : role === 'ITEM_COORDINATOR' && !selectedCoordinatorFest
        ? 'ITEM_COORDINATOR_NO_FEST'
        : role === 'VOLUNTEER' && !selectedVolunteerFest
          ? 'VOLUNTEER_NO_FEST'
          : role === 'CUSTOM' && !selectedCustomRoleFest
            ? 'CUSTOM_NO_FEST'
            : role;
  const rawNavGroups = isFestManage ? getFestManageNavGroups() : getNavGroups(effectiveRole);
  const navGroups = rawNavGroups.map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (!item.module || !item.action) return true;
      return hasPermission(item.module, item.action);
    })
  })).filter(group => group.items.length > 0);

  // Find the most specific (longest) nav key that matches the current suffix
  const allNavKeys = navGroups.flatMap(g => g.items.map(i => i.key));
  const bestMatchKey = isFestManage
    ? allNavKeys
      .filter(k => festManageSuffix === k || festManageSuffix.startsWith(k + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? null
    : allNavKeys
      .filter(k => nonManagePathSuffix === k || nonManagePathSuffix.startsWith(k + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? null;

  return (
    <>
    {/* Mobile backdrop */}
    {mobileOpen && (
      <div
        className="fixed inset-0 z-[49] bg-black/50 backdrop-blur-sm md:hidden"
        onClick={onMobileClose}
      />
    )}
    <aside
      className={`app-sidebar fixed left-0 top-0 bottom-0 z-50 flex flex-col overflow-hidden transition-[width,transform] duration-200
        ${collapsed ? 'w-[72px]' : 'w-[250px]'}
        sidebar-mobile ${mobileOpen ? 'sidebar-mobile-open' : ''}`}
      style={{ background: 'var(--sidebar-gradient, var(--sidebar))', boxShadow: '1px 0 0 var(--sidebar-border)' }}
    >
      {/* Corner gradient accents (theme-aware) */}
      <div className="pointer-events-none absolute top-0 left-0 h-48 w-48 rounded-full" style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 12%, transparent) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute bottom-0 right-0 h-48 w-48 rounded-full" style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 8%, transparent) 0%, transparent 70%)' }} />
      {/* Logo + Toggle */}
      <div
        className={`flex h-16 shrink-0 items-center ${collapsed ? 'justify-center px-0' : 'justify-between px-5'}`}
      >
        {!collapsed && <BrandLogo variant="lockup" height={28} />}
        <button
          onClick={() => onCollapse(!collapsed)}
          className="flex items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/40 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>
      </div>

      {/* Fest Manage Header */}
      {isFestManage && (() => {
        const festStatus = currentFest ? deriveFestStatus(currentFest) : null;
        const statusColor = festStatus
          ? (festStatus.color === 'green' ? '#52c41a'
            : festStatus.color === 'red' ? '#ff4d4f'
              : festStatus.color === 'blue' ? '#1677ff'
                : festStatus.color === 'purple' ? 'var(--sidebar-foreground)'
                  : '#8c8c8c')
          : '#8c8c8c';
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Back to Fests" placement="right">
                <button
                  onClick={() => setExitModalOpen(true)}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => setExitModalOpen(true)}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Back to Fests</span>
                </button>
                {currentFest && (
                  <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                    {currentFest.logo ? (
                      <img
                        src={currentFest.logo.startsWith('http') ? currentFest.logo : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${currentFest.logo}`}
                        alt={currentFest.name}
                        className="h-9 w-9 rounded-lg object-contain border border-sidebar-border bg-sidebar shrink-0"
                      />
                    ) : (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                        {currentFest.name?.charAt(0)?.toUpperCase() || 'F'}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={currentFest.name}>
                        {currentFest.name}
                      </div>
                      {festStatus && (
                        <span
                          className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: statusColor, background: `color-mix(in srgb, ${statusColor} 15%, transparent)` }}
                        >
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusColor }} />
                          {festStatus.label}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Team Lead Fest Context */}
      {role === 'TEAM_LEAD' && selectedAssignment && (() => {
        const tlStatus = deriveFestStatus({ status: selectedAssignment.festStatus ?? '', startDate: selectedAssignment.festStartDate ?? '', endDate: selectedAssignment.festEndDate ?? '' });
        const tlStatusColor = tlStatus.color === 'green' ? '#52c41a'
          : tlStatus.color === 'red' ? '#ff4d4f'
            : tlStatus.color === 'blue' ? '#1677ff'
              : tlStatus.color === 'purple' ? 'var(--sidebar-foreground)'
                : '#8c8c8c';
        const logoUrl = selectedAssignment.festLogo
          ? (selectedAssignment.festLogo.startsWith('http') ? selectedAssignment.festLogo : `${API_BASE}${selectedAssignment.festLogo}`)
          : null;
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Back to Fests" placement="right">
                <button
                  onClick={() => confirmChangeFest(selectedAssignment.festName, () => { dispatch(clearSelectedAssignment()); navigate(`${basePath}/my-fests`); })}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => confirmChangeFest(selectedAssignment.festName, () => { dispatch(clearSelectedAssignment()); navigate(`${basePath}/my-fests`); })}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Back to Fests</span>
                </button>
                <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={selectedAssignment.festName}
                      className="h-9 w-9 rounded-lg object-contain border border-sidebar-border bg-sidebar shrink-0"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                      {selectedAssignment.festName?.charAt(0)?.toUpperCase() || 'F'}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={selectedAssignment.festName}>
                      {selectedAssignment.festName}
                    </div>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: tlStatusColor, background: `color-mix(in srgb, ${tlStatusColor} 15%, transparent)` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tlStatusColor }} />
                      {tlStatus.label}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Judge Fest Context */}
      {role === 'JUDGE' && selectedJudgeFest && (() => {
        const jStatus = deriveFestStatus({ status: selectedJudgeFest.status, startDate: selectedJudgeFest.startDate, endDate: selectedJudgeFest.endDate });
        const jStatusColor = jStatus.color === 'green' ? '#52c41a'
          : jStatus.color === 'red' ? '#ff4d4f'
            : jStatus.color === 'blue' ? '#1677ff'
              : jStatus.color === 'purple' ? 'var(--sidebar-foreground)'
                : '#8c8c8c';
        const logoUrl = selectedJudgeFest.logo
          ? (selectedJudgeFest.logo.startsWith('http') ? selectedJudgeFest.logo : `${API_BASE}${selectedJudgeFest.logo}`)
          : null;
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Change Fest" placement="right">
                <button
                  onClick={() => confirmChangeFest(selectedJudgeFest.name, () => { dispatch(clearSelectedJudgeFest()); navigate('/judge/fests'); })}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => confirmChangeFest(selectedJudgeFest.name, () => { dispatch(clearSelectedJudgeFest()); navigate('/judge/fests'); })}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Change Fest</span>
                </button>
                <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={selectedJudgeFest.name}
                      className="h-9 w-9 rounded-lg object-contain border border-sidebar-border bg-sidebar shrink-0"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                      {selectedJudgeFest.name?.charAt(0)?.toUpperCase() || 'F'}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={selectedJudgeFest.name}>
                      {selectedJudgeFest.name}
                    </div>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: jStatusColor, background: `color-mix(in srgb, ${jStatusColor} 15%, transparent)` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: jStatusColor }} />
                      {jStatus.label}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Coordinator Fest Context */}
      {role === 'ITEM_COORDINATOR' && selectedCoordinatorFest && (() => {
        const cStatus = deriveFestStatus({ status: selectedCoordinatorFest.status, startDate: selectedCoordinatorFest.startDate, endDate: selectedCoordinatorFest.endDate });
        const cStatusColor = cStatus.color === 'green' ? '#52c41a'
          : cStatus.color === 'red' ? '#ff4d4f'
            : cStatus.color === 'blue' ? '#1677ff'
              : cStatus.color === 'purple' ? 'var(--sidebar-foreground)'
                : '#8c8c8c';
        const logoUrl = selectedCoordinatorFest.logo
          ? (selectedCoordinatorFest.logo.startsWith('http') ? selectedCoordinatorFest.logo : `${API_BASE}${selectedCoordinatorFest.logo}`)
          : null;
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Change Fest" placement="right">
                <button
                  onClick={() => confirmChangeFest(selectedCoordinatorFest.name, () => { dispatch(clearSelectedCoordinatorFest()); navigate('/coordinator/fests', { state: { forceSelect: true } }); })}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => confirmChangeFest(selectedCoordinatorFest.name, () => { dispatch(clearSelectedCoordinatorFest()); navigate('/coordinator/fests', { state: { forceSelect: true } }); })}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Change Fest</span>
                </button>
                <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={selectedCoordinatorFest.name}
                      className="h-9 w-9 rounded-lg object-contain border border-sidebar-border bg-sidebar shrink-0"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                      {selectedCoordinatorFest.name?.charAt(0)?.toUpperCase() || 'F'}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={selectedCoordinatorFest.name}>
                      {selectedCoordinatorFest.name}
                    </div>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: cStatusColor, background: `color-mix(in srgb, ${cStatusColor} 15%, transparent)` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: cStatusColor }} />
                      {cStatus.label}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Volunteer Fest Context */}
      {role === 'VOLUNTEER' && selectedVolunteerFest && (() => {
        const vStatus = deriveFestStatus({ status: selectedVolunteerFest.status, startDate: selectedVolunteerFest.startDate, endDate: selectedVolunteerFest.endDate });
        const vStatusColor = vStatus.color === 'green' ? '#52c41a'
          : vStatus.color === 'red' ? '#ff4d4f'
            : vStatus.color === 'blue' ? '#1677ff'
              : vStatus.color === 'purple' ? 'var(--sidebar-foreground)'
                : '#8c8c8c';
        const logoUrl = selectedVolunteerFest.logo
          ? (selectedVolunteerFest.logo.startsWith('http') ? selectedVolunteerFest.logo : `${API_BASE}${selectedVolunteerFest.logo}`)
          : null;
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Change Fest" placement="right">
                <button
                  onClick={() => confirmChangeFest(selectedVolunteerFest.name, () => { dispatch(clearSelectedVolunteerFest()); navigate('/volunteer/fests', { state: { forceSelect: true } }); })}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => confirmChangeFest(selectedVolunteerFest.name, () => { dispatch(clearSelectedVolunteerFest()); navigate('/volunteer/fests', { state: { forceSelect: true } }); })}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Change Fest</span>
                </button>
                <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={selectedVolunteerFest.name}
                      className="h-9 w-9 rounded-lg object-contain border border-sidebar-border bg-sidebar shrink-0"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                      {selectedVolunteerFest.name?.charAt(0)?.toUpperCase() || 'F'}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={selectedVolunteerFest.name}>
                      {selectedVolunteerFest.name}
                    </div>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: vStatusColor, background: `color-mix(in srgb, ${vStatusColor} 15%, transparent)` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: vStatusColor }} />
                      {vStatus.label}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Custom Role Fest Context */}
      {role === 'CUSTOM' && selectedCustomRoleFest && (() => {
        const crStatus = deriveFestStatus({ status: selectedCustomRoleFest.status, startDate: selectedCustomRoleFest.startDate, endDate: selectedCustomRoleFest.endDate });
        const crStatusColor = crStatus.color === 'green' ? '#52c41a'
          : crStatus.color === 'red' ? '#ff4d4f'
            : crStatus.color === 'blue' ? '#1677ff'
              : '#8c8c8c';
        const slug = (user as any)?.roleName ? (user as any).roleName.toLowerCase().replace(/\s+/g, '-') : 'custom';
        return (
          <div className={`shrink-0 border-b border-sidebar-border ${collapsed ? 'px-2 py-3 text-center' : 'px-5 py-3'}`}>
            {collapsed ? (
              <Tooltip title="Change Fest" placement="right">
                <button
                  onClick={() => confirmChangeFest(selectedCustomRoleFest.name, () => { dispatch(clearCustomRoleFest()); navigate(`/${slug}/fests`, { state: { forceSelect: true } }); })}
                  className="flex w-full items-center justify-center rounded-lg p-1.5 text-sidebar-foreground/55 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
            ) : (
              <>
                <button
                  onClick={() => confirmChangeFest(selectedCustomRoleFest.name, () => { dispatch(clearCustomRoleFest()); navigate(`/${slug}/fests`, { state: { forceSelect: true } }); })}
                  className="mb-2.5 flex items-center gap-2 text-[12px] font-medium text-sidebar-foreground/45 transition-colors hover:text-sidebar-foreground"
                >
                  <ArrowLeftOutlined className="text-[10px]" />
                  <span>Change Fest</span>
                </button>
                <div className="flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar text-[14px] font-bold text-sidebar-foreground/70">
                    {selectedCustomRoleFest.name?.charAt(0)?.toUpperCase() || 'F'}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold leading-tight text-sidebar-foreground truncate" title={selectedCustomRoleFest.name}>
                      {selectedCustomRoleFest.name}
                    </div>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      style={{ color: crStatusColor, background: `color-mix(in srgb, ${crStatusColor} 15%, transparent)` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: crStatusColor }} />
                      {crStatus.label}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Navigation */}
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4">
        {navGroups.map((group) => (
          <div key={group.title} className="mb-5">
            {!collapsed && (
              <div className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/45">
                {group.title}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = item.key === bestMatchKey;
                const btn = (
                  <button
                    key={item.key}
                    onClick={() => { navigate(`${effectiveBasePath}/${item.key}`); onMobileClose?.(); }}
                    className={`relative flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13.5px] transition-all ${isActive
                      ? 'font-semibold'
                      : 'font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                      } ${collapsed ? 'justify-center px-0' : ''}`}
                    style={isActive ? {
                      background: 'color-mix(in srgb, var(--primary) 12%, transparent)',
                      color: 'var(--primary)',
                    } : undefined}
                  >
                    {isActive && !collapsed && (
                      <span aria-hidden className="absolute left-0 top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-r-full" style={{ background: 'var(--primary)' }} />
                    )}
                    <span className={`text-[16px] ${collapsed ? '' : 'w-5 text-center'}`}>
                      {item.icon}
                    </span>
                    {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                  </button>
                );

                return collapsed ? (
                  <Tooltip key={item.key} title={item.label} placement="right">
                    {btn}
                  </Tooltip>
                ) : (
                  <React.Fragment key={item.key}>{btn}</React.Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User Profile Footer */}
      <div className="shrink-0 border-t border-sidebar-border px-3 py-3">
        <div
          className={`flex items-center gap-2.5 ${collapsed ? 'justify-center' : ''}`}
        >
          <Tooltip title="View Profile" placement="right">
            <button
              type="button"
              onClick={() => setProfileDrawerOpen(true)}
              className="flex shrink-0 items-center justify-center rounded-full focus:outline-none hover:opacity-80 transition-opacity"
              style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {role === 'INSTITUTION_ADMIN' && institutionLogo ? (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg overflow-hidden border border-sidebar-border bg-sidebar-accent">
                  <img src={toAbsUrl(institutionLogo)!} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
                </div>
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </button>
          </Tooltip>
          {!collapsed && (
            <>
              <button
                type="button"
                onClick={() => setProfileDrawerOpen(true)}
                className="min-w-0 flex-1 text-left focus:outline-none hover:opacity-80 transition-opacity"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <div className="truncate text-[13px] font-semibold leading-[18px] text-sidebar-foreground">
                  {user?.name}
                </div>
                <div className="text-[11px] leading-4 text-sidebar-foreground/40">
                  {role === 'CUSTOM' && (user as any)?.roleName
                    ? (user as any).roleName
                    : getRoleLabel(role)}
                </div>
              </button>
              <Tooltip title="Change Password" placement="top">
                <button
                  onClick={() => setChangePasswordOpen(true)}
                  className="flex shrink-0 items-center p-1 text-sidebar-foreground/35 transition-colors hover:text-sidebar-primary"
                >
                  <LockOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Logout" placement="right">
                <button
                  onClick={onLogout}
                  className="flex shrink-0 items-center p-1 text-sidebar-foreground/35 transition-colors hover:text-destructive"
                >
                  <LogoutOutlined />
                </button>
              </Tooltip>
            </>
          )}
        </div>
        {collapsed && (
          <>
            <Tooltip title="Change Password" placement="right">
              <button
                onClick={() => setChangePasswordOpen(true)}
                className="mt-2 flex w-full items-center justify-center py-1 text-sidebar-foreground/35 transition-colors hover:text-sidebar-primary"
              >
                <LockOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <button
                onClick={onLogout}
                className="mt-1 flex w-full items-center justify-center py-1 text-sidebar-foreground/35 transition-colors hover:text-destructive"
              >
                <LogoutOutlined />
              </button>
            </Tooltip>
          </>
        )}
      </div>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <ProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
      />

      {/* Exit Fest Management Confirmation */}
      <Modal
        open={exitModalOpen}
        onCancel={() => setExitModalOpen(false)}
        footer={null}
        centered
        width={400}
        closable={false}
      >
        <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <ArrowLeftOutlined style={{ fontSize: 24, color: 'var(--primary)' }} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6, color: 'var(--foreground)' }}>
            Exit Festival Management?
          </h3>
          <p style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 24, lineHeight: 1.5 }}>
            Are you sure you want to exit from &ldquo;{currentFest?.name}&rdquo;?
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setExitModalOpen(false)}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--foreground)', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Stay
            </button>
            <button
              onClick={() => {
                setExitModalOpen(false);
                navigate(`${basePath}/fests`);
              }}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: 'none', background: 'var(--primary)',
                color: 'var(--primary-foreground)', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Exit
            </button>
          </div>
        </div>
      </Modal>

      {/* Change Fest Confirmation (Coordinator / Volunteer / Judge / Team Lead) */}
      <Modal
        open={!!changeFestPending}
        onCancel={() => setChangeFestPending(null)}
        footer={null}
        centered
        width={400}
        closable={false}
      >
        <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <ArrowLeftOutlined style={{ fontSize: 24, color: 'var(--primary)' }} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6, color: 'var(--foreground)' }}>
            Change Fest?
          </h3>
          <p style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 24, lineHeight: 1.5 }}>
            Are you sure you want to exit from &ldquo;{changeFestPending?.festName}&rdquo;?
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setChangeFestPending(null)}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--foreground)', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Stay
            </button>
            <button
              onClick={() => { changeFestPending?.action(); setChangeFestPending(null); }}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: 'none', background: 'var(--primary)',
                color: 'var(--primary-foreground)', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Exit
            </button>
          </div>
        </div>
      </Modal>
    </aside>
    </>
  );
};

export default SideBar;
